import { FoodsThemeOptionsProvider, Themes, isTheme, themes } from "@foodpilot/foods";
import { createTheme, ThemeProvider, ThemeOptions } from "@mui/material";
import { useApplicationParameters } from "./api/client-api/appParams";
import { useCustomTheme } from "./api/client-api/customTheme/useCustomTheme";
import { formatNumber } from "./utils/formatting";
import { useLanguage } from "./context/LanguageProvider";

type ThemedAppProps = {
    children: ReactNode;
};

export const ThemedApp = (props: ThemedAppProps) => {
    const { applicationParameters } = useApplicationParameters();
    const { customThemeConfig, isLoading: isCustomThemeLoading } = useCustomTheme();
    const { currentLanguage } = useLanguage();

    const themeParameter =
        customThemeConfig?.hasCustomTheme ?
            (customThemeConfig.config?.baseTheme ?? applicationParameters?.theme ?? "")
        :   (applicationParameters?.theme ?? "");

    const theme: Themes = isTheme(themeParameter) ? themeParameter : "foodpilot";

    const baseThemeOverride = {
        typography: {},
        palette: {},
        components: {},
        custom: {},
        numbers: {
            valueFormatterFn: (value, _precision) => {
                return formatNumber(value, currentLanguage);
            },
        },
    } as ThemeOptions;

    const themeOverride: ThemeOptions = (() => {
        if (!customThemeConfig?.hasCustomTheme || !customThemeConfig.config) {
            return baseThemeOverride;
        }

        const customConfig = customThemeConfig.config;

        return {
            ...baseThemeOverride,
            ...(customConfig.palette && {
                palette: {
                    ...baseThemeOverride.palette,
                    ...(customConfig.palette.primary && {
                        primary: customConfig.palette.primary,
                    }),
                    ...(customConfig.palette.secondary && {
                        secondary: customConfig.palette.secondary,
                    }),
                },
            }),
            ...(customConfig.typography && {
                typography: {
                    ...baseThemeOverride.typography,
                    ...customConfig.typography,
                },
            }),
            ...(customConfig.spacing && {
                spacing: customConfig.spacing,
            }),
            ...(customConfig.components && {
                components: {
                    ...baseThemeOverride.components,
                    ...customConfig.components,
                },
            }),
            ...(customConfig.custom && {
                custom: {
                    ...baseThemeOverride.custom,
                    ...customConfig.custom,
                },
            }),
        } as ThemeOptions;
    })();

    const localTheme = () => {
        return createTheme(themes[theme], themeOverride);
    };

    if (isCustomThemeLoading) {
        return null;
    }

    return (
        <FoodsThemeOptionsProvider theme={theme} themeOverride={themeOverride}>
            <ThemeProvider theme={localTheme()}>{props.children}</ThemeProvider>
        </FoodsThemeOptionsProvider>
    );
};
